import React, { memo } from 'react'
import * as rn from 'react-native'

import * as c from '../../common'
import * as r from '../../react-utils'

import Icon from 'react-native-vector-icons/MaterialCommunityIcons'

import * as gs from '../gStyles'

import Select from './Select'
import NumberInput from './NumberInput'
import Text from './Text'
import TextInput, { TextInputProps } from './TextInput'

import { rowColsToDraw } from './rowInputCols'
import View from './View'

export interface RowInputProps<K extends c.FieldRowable> {
  readonly customerID: string
  readonly disabled?: boolean
  readonly on: c.Surface
  readonly field: K
}

export default React.memo(function ConnRowInput<K extends c.FieldRowable>({
  customerID,
  disabled,
  on,
  field,
}: RowInputProps<K>) {
  const {
    addRow,
    changeVal,
    cols: allCols,
    deleteRow,
    rows,
  } = r.useRowable(customerID, field)
  const cols = React.useMemo(
    () => rowColsToDraw(allCols, field, r.isMobile),
    [allCols, field],
  )
  // Only show headings for first line
  const firstLineCols = React.useMemo(() => {
    const _firstLineCols: c.Col[] = []

    let sum = 0
    for (const col of cols) {
      if (sum < 90) {
        _firstLineCols.push(col)
      } else {
        break
      }
      sum += col.widthPer
    }

    return _firstLineCols
  }, [cols])

  //#region styling
  const _colorScheme = rn.useColorScheme()
  const colorScheme = _colorScheme === 'unspecified' ? 'light' : _colorScheme
  const styles = gs.useThemedStyleSheet(themedStyles, on, colorScheme)
  const rowGap = gs.useRowGap(on)
  const headerStyles = React.useMemo(
    () =>
      firstLineCols.map(
        (c): rn.ViewStyle => ({
          // backgroundColor: rndColor(),
          width: `${c.widthPer}%`,
        }),
      ),
    [firstLineCols],
  )
  //#endregion
  //#region global
  const [customer] = r.useCustomer(customerID)
  //#endregion

  const fieldLabel = React.useMemo(() => c.getFieldLabel(field), [field])

  const cellStyles = React.useMemo((): rn.ViewStyle[] => {
    let sum = 0

    const styles = cols.map((c): rn.ViewStyle => {
      sum += c.widthPer
      return {
        // ...gStyles.deadCenter,
        // backgroundColor: rndColor(),
        marginRight: '4%' as const,
        width: `${c.widthPer - 4}%`,
      }
    })

    if (sum !== 90 && sum > 190) {
      console.warn(
        `Column widths must either add up to 90 (percent) or be higher than 90 but lower than 190, got: ${cols
          .map((c) => c.widthPer)
          .toString()}`,
      )
    }

    return styles
  }, [cols])

  const rowMapper = React.useCallback(
    (row: c.Rowable, _: number): React.ReactElement => (
      <React.Fragment key={row.id}>
        <Row
          cellStyles={cellStyles}
          cols={cols}
          preferBelow={r.isWeb && SECURITY_PRODUCT_FIELDS.includes(field)}
          disabled={disabled}
          headerStyles={headerStyles}
          on={on}
          onChangeValue={changeVal}
          onDeleteRow={deleteRow}
          row={row}
        />

        {rowGap}
      </React.Fragment>
    ),
    [
      cellStyles,
      cols,
      headerStyles,
      on,
      changeVal,
      deleteRow,
      rowGap,
      disabled,
      field,
    ],
  )

  const moreThanOneLine = cols.length > firstLineCols.length

  return (
    <rn.View
      style={
        c.shouldHideInput(field, customer)
          ? styles.rootHidden
          : disabled
          ? styles.rootDisabled
          : styles.root
      }
    >
      {fieldLabel !== field && <Text style={styles.label}>{fieldLabel}</Text>}

      {fieldLabel !== field && rowGap}

      {!moreThanOneLine && (
        <>
          <View marginLeft="-2%" style={gs.row}>
            {firstLineCols.map((col, colIdx) => (
              <rn.View key={customerID + col.key} style={headerStyles[colIdx]}>
                <Text centerText on={on} size={14}>
                  {col.label}
                </Text>
              </rn.View>
            ))}

            {/* Delete column */}
            <rn.View style={gs.width10} />
          </View>

          {rowGap}
        </>
      )}

      {rows.map(rowMapper)}

      {!disabled && (
        <>
          <rn.Pressable
            onPress={addRow}
            style={({ pressed }) => (pressed ? gs.pressedOpacity : undefined)}
          >
            <rn.View style={styles.buttonAdd}>
              <Icon
                color={c.themeTuple[colorScheme][on].color as string}
                name="plus"
                size={20}
                style={gs.userSelectNone}
              />
            </rn.View>
          </rn.Pressable>
        </>
      )}
    </rn.View>
  )
})

const SECURITY_PRODUCT_FIELDS: readonly string[] = [
  'security_cams_detail',
  'security_wifi_cameras_detail',
  'security_system_detail',
  'security_home_automation_detail',
  'security_tv_detail',
]

interface RowProps {
  readonly cellStyles: readonly rn.ViewStyle[]
  readonly cols: c.Cols
  readonly preferBelow?: boolean
  readonly disabled?: boolean
  readonly headerStyles: readonly rn.ViewStyle[]
  readonly onChangeValue: (
    col: string,
    rowID: string,
    val: React.SetStateAction<number | string>,
  ) => void
  readonly on: c.Surface
  readonly onDeleteRow: (rowID: string) => void
  readonly row: c.Rowable
}

const Row = React.memo<RowProps>(function Row({
  cellStyles,
  cols,
  preferBelow,
  disabled,
  headerStyles,
  on,
  row,
  onChangeValue,
  onDeleteRow,
}) {
  const t = r.useTheme()
  const styles = gs.useThemedStyleSheet(themedStyles, on)
  const rowGap = gs.useRowGap(on)

  const handleDeleteRow = React.useCallback(
    (): void => void onDeleteRow(row.id),
    [onDeleteRow, row.id],
  )

  const [firstLine, secondLine] = React.useMemo(() => {
    const _firstLine: c.Col[] = []
    const _secondLine: c.Col[] = []

    let sum = 0
    for (const col of cols) {
      if (sum < 90) {
        _firstLine.push(col)
      } else {
        _secondLine.push(col)
      }
      sum += col.widthPer
    }

    return [_firstLine, _secondLine]
  }, [cols])

  const handleChangeValueFirstLine = React.useMemo(
    () =>
      firstLine.map((col) => (value: React.SetStateAction<number | string>) => {
        onChangeValue(col.key, row.id, value)
      }),
    [firstLine, onChangeValue, row.id],
  )
  const handleChangeValueSecondLine = React.useMemo(
    () =>
      secondLine.map(
        (col) => (value: React.SetStateAction<number | string>) => {
          onChangeValue(col.key, row.id, value)
        },
      ),
    [secondLine, onChangeValue, row.id],
  )

  if (cols.length === 0) {
    return null
  }

  const firstLineEl = (
    <rn.View style={gs.row}>
      {firstLine.map((col, colIdx) => (
        <Cell
          col={col}
          preferBelow={preferBelow}
          disabled={disabled}
          key={row.id + '>' + 0 + '>' + col.key}
          on={on}
          onChange={handleChangeValueFirstLine[colIdx]!}
          row={row}
          style={cellStyles[colIdx]}
        />
      ))}

      <rn.View style={styles.rowRemove}>
        <rn.TouchableOpacity disabled={disabled} onPress={handleDeleteRow}>
          <Icon color={t.danger} name="close" size={16} />
        </rn.TouchableOpacity>
      </rn.View>
    </rn.View>
  )

  if (secondLine.length > 0) {
    return (
      <rn.View style={styles.multiRow}>
        {secondLine.length > 0 && (
          <>
            <View marginLeft="-2%" style={gs.row}>
              {firstLine.map((col, colIdx) => (
                <rn.View
                  key={row.id + '>heading>' + col.key}
                  style={headerStyles[colIdx]}
                >
                  <Text centerText on={on} size={14}>
                    {col.label}
                  </Text>
                </rn.View>
              ))}

              {/* Delete column */}
              <rn.View style={gs.width10} />
            </View>

            {rowGap}
          </>
        )}

        {firstLineEl}

        {gs.rowGap12}

        <rn.View style={gs.row}>
          {secondLine.map((col, colIdx) => (
            <Cell
              col={col}
              preferBelow={preferBelow}
              disabled={disabled}
              key={row.id + '>' + 0 + '>' + col.key}
              on={on}
              onChange={handleChangeValueSecondLine[colIdx]!}
              row={row}
              style={cellStyles[colIdx]}
            />
          ))}
        </rn.View>
      </rn.View>
    )
  }

  return firstLineEl
})

interface CellProps {
  readonly col: c.Col
  readonly preferBelow?: boolean
  readonly disabled?: boolean
  readonly on: c.Surface
  readonly style: rn.StyleProp<rn.ViewStyle>
  readonly row: c.Rowable
  readonly onChange: React.Dispatch<React.SetStateAction<number | string>>
}

const Cell = memo<CellProps>(
  ({ col, preferBelow, disabled, on, onChange, row, style }) => {
    const styles = gs.useThemedStyleSheet(themedStyles, on)
    const colGap = gs.useColGap(on)

    const multiInputCurrValues: c.strings =
      col.type === 'multi-input'
        ? JSON.parse(row[col.key as keyof typeof row])
        : c.emptyArr
    const multiInputHandleChange =
      React.useMemo((): readonly TextInputProps['onChangeText'][] => {
        if (col.type !== 'multi-input') {
          return c.EMPTY_ARRAY
        }
        return c
          .range(c.parseInt((row as any).qty))
          .map((_, i) => (newVal: string) => {
            onChange((currValuesStr) => {
              const currValues = JSON.parse(currValuesStr as string)
              currValues[i] = newVal
              return JSON.stringify(currValues)
            })
          })
      }, [col.type, onChange, row])

    return (
      <rn.View style={style}>
        {col.type === 'calc' && (
          <rn.View style={gs.deadCenterGrow}>
            <Text style={styles.calc}>{col.calc(row).toFixed(2)}</Text>
          </rn.View>
        )}
        {col.type === 'input' && (
          <TextInput
            editable={!disabled}
            keyboardType="decimal-pad"
            noMinWidth
            on={on}
            onChangeText={onChange}
            value={
              c.zeroes.includes(row[col.key as keyof typeof row])
                ? ''
                : String(row[col.key as keyof typeof row])
            }
          />
        )}
        {col.type === 'multi-input' &&
          !!(row as any).qty &&
          c.range(c.parseInteger((row as any).qty)).map((_, i, { length }) => (
            <rn.View
              key={`${row.id}>${col.key}>${i}`}
              style={styles.multiInputRow}
            >
              <Text on={on}>
                {col.label}
                {length > 1 && ` (${String.fromCharCode(65 + i)})`}:
              </Text>
              {colGap}
              <TextInput
                editable={!disabled}
                on={on}
                onChangeText={multiInputHandleChange[i]}
                value={multiInputCurrValues[i]}
              />
            </rn.View>
          ))}
        {col.type === 'notes-input' && (
          <rn.View style={styles.notes}>
            <Text on={on}>Notes:</Text>
            {colGap}
            <TextInput
              editable={!disabled}
              growOnFocus
              on={on}
              onChangeText={onChange}
              shrink // Somehow makes it grow?
              value={row[col.key as keyof typeof row]}
            />
          </rn.View>
        )}
        {col.type === 'number-input' && (
          <NumberInput
            disabled={disabled}
            on={on}
            onChange={onChange}
            value={row[col.key as keyof typeof row]}
          />
        )}
        {col.type === 'opts' && (
          <Select
            disabled={disabled}
            grow
            on={on}
            onChange={onChange}
            opts={col.opts}
            preferBelow={preferBelow}
            value={row[col.key as keyof typeof row]}
          />
        )}
      </rn.View>
    )
  },
)

const themedStyles = gs.ThemedStyleSheet.create((t, on, colorScheme) => {
  const flippedColorScheme = colorScheme === 'dark' ? 'light' : 'dark'

  return {
    buttonAdd: {
      alignItems: 'center',
      alignSelf: 'center',
      backgroundColor: c.themeTuple[flippedColorScheme][on].color,
      borderRadius: Number.MAX_SAFE_INTEGER,
      justifyContent: 'center',
      padding: 12,
    },
    calc: {
      color: t.input[on].color,
      fontFamily: t.input[on].fontFamily,
      fontSize: t.input[on].fontSize,
      fontWeight: t.input[on].fontWeight,
      textAlign: 'center',
      textAlignVertical: 'center',
      // @ts-ignore
      userSelect: 'none',
    },
    label: {
      color: t[on].color,
      fontFamily: c.light.fontFamily,
      fontSize: 18,
      // @ts-ignore
      userSelect: 'none',
    },
    multiInputRow: {
      //@ts-ignore
      gap: c.IS_WEB ? t[on].gap : (0 as c.Gap),
    },
    multiRow: {
      //@ts-ignore
      gap: c.IS_WEB ? t[on].gap : (0 as c.Gap),
    },
    notes: {
      ...gs.rowCentered,
      //@ts-ignore
      gap: c.IS_WEB ? t[on].gap : (0 as c.Gap),
      marginTop: t[on].gap,
      paddingLeft: t[on].gap,
    },
    root: {
      //@ts-ignore
      gap: t[on].gap,
      ...gs.inputGrowNew,
    },
    rootDisabled: {
      //@ts-ignore
      gap: c.IS_WEB ? t[on].gap : (0 as c.Gap),
      ...gs.inputGrowNew,
      ...gs.disabled,
    },
    rootHidden: {
      //@ts-ignore
      gap: c.IS_WEB ? t[on].gap : (0 as c.Gap),
      ...gs.inputGrowNewHidden,
    },
    rowRemove: { ...gs.deadCenter, width: '6%' },
    textGray: { color: 'rgba(0,0,0,0.4)' },
    textGrayDark: { color: 'rgba(255,255,255,0.4)' },
  }
})
